import { useCallback, useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { batch, useDispatch } from 'react-redux';

import { BrandliveEvent, EPermissions, PERMISSIONS, Session, SessionTypesFeatures } from '../../../types/working-model';
import Icon, { COLORS, ICONS } from '../../general-ui/icon';
import TellyMarch from '../../../images/telly-march.svg';
import { useTypedSelector } from '../../../store/reducers/use-typed-selector';
import ModalComponent from '../../general-ui/modal/modal';
import NavigationDropdown from '../../general-ui/dropdown/navigation-dropdown';
import ButtonLoader from '../../general-ui/button/button-loader';
import { DeleteEvent, DeleteSession } from '../../../connection/create-event';
import { removeEventFromCreateEventReducer, removeSessionFromCreateEventReducer } from '../../../store/actions/admin/create-event';
import { getEvents } from '../../../store/actions/admin/events';
import { removeSessionFromCreateSessionReducer } from '../../../store/actions/admin/create-event/session';
import { showAlert } from '../../general-ui/alert/alert-service';
import { AuthWrapper } from '../authentication/AuthWrapper';

import { PATHNAMES } from '../admin';
interface Props {
	session: Session;
	selectedEvent: BrandliveEvent;
}

export default function SessionItem(props: Props): JSX.Element {
	const { session, selectedEvent } = props;

	const [isLive, setIsLive] = useState(false);
	const [showModeratorError, setShowModeratorError] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);

	const user = useTypedSelector(state => state.AuthReducer.user);
	const token = useTypedSelector(state => state.AuthReducer.token);
	const deletingSession = useTypedSelector(state => state.CreateEventReducer.deletingSession);

	const dispatch = useDispatch();
	const history = useHistory();

	function formatTime(date: Date) {
		return new Date(date).toLocaleString(navigator.language, { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: 'numeric' });
	}

	// need to push the event route before the session route for correct back navigation within the editor
	const getSessionLink = (session: Session): void => {
		history.push(PATHNAMES.Event.EventLink(selectedEvent.uuid));
		history.push(PATHNAMES.Session.SessionLink(selectedEvent.uuid, session.uuid, session.default_language));
	};

	useEffect(() => {
		setIsLive(false);
		const date = Date.now();
		const timestamp = new Date(session.timestamp!);
		const end_timestamp = new Date(session.end_timestamp!);
		if (timestamp.getTime() < date && end_timestamp.getTime() > date) {
			setIsLive(true);
		}
	}, [session.timestamp, session.end_timestamp]);

	const handleModerateLink = (e: React.MouseEvent<HTMLAnchorElement>) => {
		const allowedRoles: any = {
			[EPermissions.Admin]: PERMISSIONS.Admin,
			[EPermissions.Moderation]: PERMISSIONS.Moderation,
			[EPermissions.Owner]: PERMISSIONS.Owner,
		};
		// if user does not include Owner, Admin or Moderator roles, then preventDefault and show error message
		// else do nothing and let navigation do its thing
		const userChannelRoles = user?.channels?.[user?.active_channel];
		if (!userChannelRoles || !userChannelRoles.find((ucr) => allowedRoles[ucr])) {
			e.preventDefault();
			setShowModeratorError(true);
		}
	};

	const closeModerator = useCallback(() => {
		setShowModeratorError(false);
	}, []);

	const handleDeleteSession = () => setShowDeleteModal(true);

	const closeDeleteSessionModal = () => setShowDeleteModal(false);

	const confirmDeleteSessionModal = async () => {
		if (!session?.session || !selectedEvent?.uuid || !token || !user?.active_channel) return;
		setIsDeleting(true);
		if (selectedEvent?.sessions?.length <= 1) {
			// Delete the entire event, update redux for this event and navigate back to main events page
			try {
				await DeleteEvent(selectedEvent.uuid, token);
				batch(() => {
					dispatch(getEvents(user.active_channel, token)); // refetch events for simplicity instead of filtering
					dispatch(removeEventFromCreateEventReducer(selectedEvent.uuid));
				});
			} catch (e) {
				showAlert({
					message: 'Error deleting event',
					description: '',
					type: 'error',
					duration: 5000,
				});
				console.error(e);
			}
		} else {
			// Delete the session only
			try {
				await DeleteSession(session.session, selectedEvent.uuid, token);
				batch(() => {
					dispatch(getEvents(user.active_channel, token)); // refetch events for simplicity instead of filtering
					dispatch(removeSessionFromCreateEventReducer(session.session, selectedEvent.uuid));
					dispatch(removeSessionFromCreateSessionReducer(session.session, selectedEvent.uuid));
				});
			} catch (e) {
				showAlert({
					message: 'Error deleting session',
					description: '',
					type: 'error',
					duration: 5000,
				});
				console.error(e);
			}
		}
		closeDeleteSessionModal();
		setIsDeleting(false);
	};

	return (
		<>
			<div key={session.uuid} className="list-item">
				<div className="item-left">
					<div className="session-image">
						<img src={session.image ? session.image : TellyMarch} />
					</div>
					<div className="session-info">
						<div className="session-time">
							{session.timestamp ? formatTime(new Date(session.timestamp)) + " — " + formatTime(new Date(session.end_timestamp!)) : <></>}
						</div>
						<div className="session-name">
							<h5>{session.title.base}</h5>
						</div>
						<div className="session-tags">
							{isLive ? <div className=" broadcast-tag live"><Icon name={ICONS.LIVE} size={12} color={COLORS.WHITE} />Live</div> : <></>}
							<div className="broadcast-tag"><Icon name={ICONS.VIEWERS_EYE} size={12} color={COLORS.WHITE} />{SessionTypesFeatures[session.session_type].display_name.toUpperCase()}</div>
							{session.tracks ? (
								session.tracks.map((track: string) => {
									return <div key={track}>{track}</div>;
								})
							) : (<></>)}
						</div>
					</div>
				</div>
				<div className="item-right">
					<AuthWrapper>
						<NavigationDropdown
							title=""
							className="sessions-more-actions"
							navdropClassName="sessions-more-actions-dropdown"
							edge="right"
							renderCustomIcon={() => (
								<Icon name={ICONS.THREE_DOTS_VERTICAL} size={16} color={COLORS.WHITE} />
							)}
						>
							{/* TODO: duplicate will need to be added after MVP */}
							{/* <button className="sharp">Duplicate</button> */}
							<ButtonLoader loading={deletingSession} disabled={deletingSession} className="sharp" onClick={handleDeleteSession}>Delete</ButtonLoader>
						</NavigationDropdown>
						<button className="button" onClick={() => getSessionLink(session)}><Icon name={ICONS.EDIT} size={12} color={COLORS.WHITE} />Edit</button>
					</AuthWrapper>
					{/* <button>Go to Greenroom</button> */}
					<Link
						onClick={handleModerateLink}
						to={`/moderator/event/${selectedEvent.uuid}/${session.default_language}/session/${session.uuid}`}
						className="theme-btn primary"
					>
						Moderate
					</Link>
				</div>
			</div>

			<ModalComponent
				open={showModeratorError}
				title="Only channel owners, administrators and moderators have access to a session's moderator page."
				onRequestClose={closeModerator}
				closeable
				cancellable
			>
				<div></div>
			</ModalComponent>

			<ModalComponent
				open={showDeleteModal}
				title={selectedEvent?.sessions?.length <= 1 ? 'DELETE EVENT?' : `Delete session ${session.title?.base || ''}?`}
				onRequestClose={closeDeleteSessionModal}
				closeable
				cancellable
				footer={(
					<div style={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}>
						<button onClick={closeDeleteSessionModal}>cancel</button>
						<ButtonLoader loading={isDeleting} disabled={isDeleting} className="primary" onClick={confirmDeleteSessionModal}>Confirm</ButtonLoader>
					</div>
				)}
			>
				{selectedEvent?.sessions?.length <= 1 ? (
					<>
						<p>Deleting this session will delete your <strong>entire event</strong>. Are you sure you want to proceed?</p>
					</>
				) : (
					<>
						<p>Deleting this session will publish any recent changes you’ve made to the event. Are you sure you want to proceed?</p>
					</>
				)}
			</ModalComponent>
		</>
	);
}